import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["allowSubmitWhileOpen", "appearance", "autoCompleteBehavior", "isDropdownPortal", "className", "editable", "mapOptionToProps", "onChange", "onOptionFocus", "onOptionSelect", "onKeyDown", "optionFilter", "fluidDropdown", "focusFirstOption", "freeForm", "getHighlightPattern", "limitTagsShown", "loading", "options", "renderEmptyState", "renderGroup", "renderGroupLabel", "renderLoadingState", "renderInput", "renderOption", "renderTag", "selectCallbackFrequency", "size", "value", "showLabelForValue"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CALLBACK_FREQUENCY, COMBOBOX_EVENT_REASON, INPUT_SIZES, KEY_CODES, LISTBOX_EVENT_REASON } from '../../constants';
import { escapeRegex } from '../../util/escapeRegex';
import { identifier } from '../../util/identifier';
import { isArrayOfArrays } from '../../util/isArrayOfArrays';
import { parseOptions } from '../../util/parseOptions';
import { toggleArrayValue } from '../../util/toggleArrayValue';
import { useDidChangeCallback } from '../../util/useDidChangeCallback';
import { useIsomorphicLayoutEffect } from '../../util/useIsomorphicLayoutEffect';
import { useResizeObserver } from '../../util/useResizeObserver';
import { ComboboxEmptyState } from '../ComboboxEmptyState/ComboboxEmptyState';
import { ComboboxInput } from '../ComboboxInput/ComboboxInput';
import { ComboboxLoadingState } from '../ComboboxLoadingState/ComboboxLoadingState';
import { ComboboxOption } from '../ComboboxOption/ComboboxOption';
import { Dropdown } from '../Dropdown/Dropdown';
import { DropdownPopper } from '../DropdownPopper/DropdownPopper';
import { ControlledListbox } from '../Listbox/ControlledListbox';
import { Tag } from '../Tag/Tag';
import { StyledCombobox } from './Combobox.styles';

// Prevent browser autofill from showing on this field.
// https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion#preventing_autofilling_with_autocompletenew-password
var AUTOFILL_DISABLE = 'new-password';

/**
 * @related [`Combobox`]
 * @since 8.28.0
 */
export var ControlledCombobox = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var allowSubmitWhileOpen = props.allowSubmitWhileOpen,
    appearance = props.appearance,
    autoCompleteBehavior = props.autoCompleteBehavior,
    isDropdownPortal = props.isDropdownPortal,
    className = props.className,
    editable = props.editable,
    mapOptionToProps = props.mapOptionToProps,
    onChange = props.onChange,
    onOptionFocus = props.onOptionFocus,
    onOptionSelect = props.onOptionSelect,
    onKeyDown = props.onKeyDown,
    optionFilterProp = props.optionFilter,
    fluidDropdown = props.fluidDropdown,
    focusFirstOption = props.focusFirstOption,
    freeForm = props.freeForm,
    getHighlightPattern = props.getHighlightPattern,
    limitTagsShown = props.limitTagsShown,
    loading = props.loading,
    optionsProp = props.options,
    renderEmptyState = props.renderEmptyState,
    renderGroup = props.renderGroup,
    renderGroupLabel = props.renderGroupLabel,
    renderLoadingState = props.renderLoadingState,
    renderInput = props.renderInput,
    renderOption = props.renderOption,
    renderTag = props.renderTag,
    selectCallbackFrequency = props.selectCallbackFrequency,
    size = props.size,
    value = props.value,
    showLabelForValue = props.showLabelForValue,
    rest = _objectWithoutProperties(props, _excluded);
  var isMultiselectable = Array.isArray(value);
  var defaultDropdownWidth = fluidDropdown ? '100%' : 'auto';
  var _useState = useState(value),
    _useState2 = _slicedToArray(_useState, 2),
    controlledValue = _useState2[0],
    setControlledValue = _useState2[1];
  var _useState3 = useState(isMultiselectable ? '' : value),
    _useState4 = _slicedToArray(_useState3, 2),
    inputValue = _useState4[0],
    setInputValue = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isDropdownOpen = _useState6[0],
    setIsDropdownOpen = _useState6[1];
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    activeOptionState = _useState8[0],
    setActiveOption = _useState8[1];
  var _useState9 = useState(null),
    _useState10 = _slicedToArray(_useState9, 2),
    activeReasonState = _useState10[0],
    setActiveReason = _useState10[1];
  var _useState11 = useState(defaultDropdownWidth),
    _useState12 = _slicedToArray(_useState11, 2),
    dropdownWidth = _useState12[0],
    setDropdownWidth = _useState12[1];
  var isInputModified;
  if (isMultiselectable || freeForm) {
    isInputModified = !!inputValue.trim();
  } else {
    isInputModified = inputValue.trim() !== value.trim();
  }

  // Create refs
  var listboxRef = useRef();
  var popperRef = useRef();
  var defaultInnerRef = useRef();
  var innerRef = forwardedRef || defaultInnerRef;

  // input resize observer
  var inputResizeObserver = useResizeObserver(innerRef);

  // Listbox id
  var listboxIdRef = useRef(identifier());
  var listboxId = listboxIdRef.current;

  // The default pattern for highlight and focus match
  var defaultPatternRegex = new RegExp("\\b" + escapeRegex(inputValue.trim()), 'ig');

  // Highlight the options that match the typed input
  var highlightPattern;
  if (typeof getHighlightPattern === 'function') {
    highlightPattern = getHighlightPattern(inputValue);
  } else if (typeof getHighlightPattern === 'undefined' && isInputModified) {
    highlightPattern = defaultPatternRegex;
  }

  // Do not focus unless the user has typed
  var focusPattern;
  if (isInputModified) {
    focusPattern = defaultPatternRegex;
  }

  // Create the option filter based on the input value
  var optionFilter;
  var optionFilterCallback = useCallback(function (option) {
    return optionFilterProp(option, inputValue);
  }, [optionFilterProp, inputValue]);
  // Do not filter unless the user has typed
  if (typeof optionFilterProp === 'function' && isInputModified) {
    optionFilter = optionFilterCallback;
  }

  // Configure the autocomplete behavior, defaulting to "automatic"
  var ariaAutocomplete = 'list';
  if (autoCompleteBehavior === 'none') {
    ariaAutocomplete = 'none';
    // Prevent focus
    focusPattern = undefined;
    // Prevent filtering
    optionFilter = undefined;
    // Prevent highlighting
    highlightPattern = undefined;
  } else if (autoCompleteBehavior === 'manual') {
    // Prevent focus
    focusPattern = undefined;
  }

  // Space key should act as a character by default
  var selectWithSpace = false;
  if (!editable) {
    // Do not filter values because you would never be able to select a new value
    optionFilter = undefined;
    // Read only input value does not change, so we must rely on listbox typeahead
    focusPattern = undefined;
    // Read only does not use space key, so we can select
    selectWithSpace = true;
  }
  var _useMemo = useMemo(function () {
      return parseOptions(optionsProp, {
        optionMap: mapOptionToProps,
        optionFilter: optionFilter
      });
    }, [optionsProp, mapOptionToProps, optionFilter]),
    options = _useMemo.parsedOptions,
    validValues = _useMemo.validValues,
    valueToOption = _useMemo.valueToOption;
  var activeOption = activeOptionState;
  var activeReason = activeReasonState;

  // Check if the active value was filtered out
  if (activeOption && !valueToOption[activeOption.value]) {
    activeOption = null;
    activeReason = null;
  }

  // Set the active option to the current value if it exists
  if (!activeOption && !isMultiselectable && value && isDropdownOpen) {
    activeOption = valueToOption[value];
  }

  // Focus the first option only if a value wasn't focused previously
  if (!activeOption && focusFirstOption && isDropdownOpen) {
    activeOption = valueToOption[validValues[0]];
  }

  /** ********
   * ACTIONS *
   ********* */
  // Given a value, return the label
  var getSelectedOptionLabel = useCallback(function (nextValue) {
    var _optionsProp$filter$;
    if (isArrayOfArrays(optionsProp)) {
      return optionsProp.reduce(function (acc, o) {
        var _o$filter$;
        var runner = _toConsumableArray(acc);
        var label = (_o$filter$ = o.filter(function (opt) {
          return opt.value === nextValue;
        })[0]) === null || _o$filter$ === void 0 ? void 0 : _o$filter$.label;
        if (label !== undefined) {
          runner.push(label);
        }
        return runner;
      }, [])[0] || nextValue;
    }
    return ((_optionsProp$filter$ = optionsProp.filter(function (opt) {
      return opt.value === nextValue;
    })[0]) === null || _optionsProp$filter$ === void 0 ? void 0 : _optionsProp$filter$.label) || nextValue;
  }, [optionsProp]);
  // Given a label, return the value
  var getSelectedOptionValue = useCallback(function (nextValue) {
    var _optionsProp$filter$2;
    if (isArrayOfArrays(optionsProp)) {
      return optionsProp.reduce(function (acc, o) {
        var _o$filter$2;
        var runner = _toConsumableArray(acc);
        var val = (_o$filter$2 = o.filter(function (opt) {
          return opt.value === nextValue;
        })[0]) === null || _o$filter$2 === void 0 ? void 0 : _o$filter$2.value;
        if (val !== undefined) {
          runner.push(val);
        }
        return runner;
      }, [])[0] || nextValue;
    }
    return ((_optionsProp$filter$2 = optionsProp.filter(function (opt) {
      return opt.label === nextValue;
    })[0]) === null || _optionsProp$filter$2 === void 0 ? void 0 : _optionsProp$filter$2.value) || nextValue;
  }, [optionsProp]);
  var selectValue = useCallback(function (val, meta) {
    var nextValue = val;
    if (isMultiselectable) {
      if (typeof nextValue === 'string') {
        nextValue = toggleArrayValue(value, nextValue);
      }
      setInputValue('');
      if (activeReason === LISTBOX_EVENT_REASON.TYPEAHEAD || activeReason === null) {
        setActiveOption(null);
        setActiveReason(null);
      }
    } else {
      setInputValue(showLabelForValue && nextValue !== '' ? getSelectedOptionLabel(nextValue) : nextValue);
      setIsDropdownOpen(false);
      setActiveOption(null);
      setActiveReason(null);
    }
    if (typeof onChange === 'function' && (nextValue !== value || meta.reason === COMBOBOX_EVENT_REASON.CONTROLLED_OVERWRITE)) {
      onChange(showLabelForValue && nextValue !== '' ? getSelectedOptionLabel(nextValue) : nextValue, meta);
    }
  }, [activeReason, getSelectedOptionLabel, isMultiselectable, onChange, showLabelForValue, value]);
  var clearValue = useCallback(function () {
    var meta = {
      reason: COMBOBOX_EVENT_REASON.CLEAR_BUTTON
    };
    if (isMultiselectable) {
      selectValue([], meta);
    } else {
      selectValue('', meta);
    }
  }, [isMultiselectable, selectValue]);
  var resetState = useCallback(function () {
    // The automatically selected suggestion becomes the value of the combobox when the combobox
    // loses focus.
    if (autoCompleteBehavior === 'automatic' && activeOption && activeReason === LISTBOX_EVENT_REASON.TYPEAHEAD && !isMultiselectable) {
      selectValue(activeOption.value, {
        reason: COMBOBOX_EVENT_REASON.AUTOCOMPLETE_AUTOMATIC,
        option: activeOption
      });
    }
    setIsDropdownOpen(false);
    setActiveOption(null);
    setActiveReason(null);
    if (isMultiselectable) {
      setInputValue('');
    } else {
      setInputValue(value);
    }
  }, [activeOption, activeReason, autoCompleteBehavior, isMultiselectable, selectValue, value]);

  /** *********
   * BEHAVIOR *
   ********** */

  // Listen for changes from the listbox
  var optionSelectCallback = useCallback(function (option, meta) {
    if (meta.valueChanged) {
      selectValue(option.value, {
        reason: COMBOBOX_EVENT_REASON.LISTBOX_EVENT,
        listboxEventReason: meta.reason,
        option: option
      });
    }
    if (typeof onOptionSelect === 'function' && meta.valueChanged || selectCallbackFrequency === CALLBACK_FREQUENCY.ALWAYS) {
      onOptionSelect(option, meta);
    }
    if (!isMultiselectable) {
      setIsDropdownOpen(false);
    }
  }, [isMultiselectable, selectCallbackFrequency, selectValue, onOptionSelect]);

  // Handle changes typed into the text input field
  var inputChangeCallback = useCallback(function (e) {
    var nextValue = e.currentTarget.value;
    if (nextValue === '') {
      setActiveOption(null);
      setActiveReason(null);
      if (!isMultiselectable) {
        selectValue('', {
          reason: COMBOBOX_EVENT_REASON.CLEAR_INPUT
        });
      }
    } else if (freeForm && !isMultiselectable) {
      selectValue(nextValue, {
        reason: COMBOBOX_EVENT_REASON.FREEFORM
      });
    }
    setIsDropdownOpen(true);
    setInputValue(nextValue);
  }, [freeForm, isMultiselectable, selectValue]);

  // Clear the input on blur
  var inputBlurCallback = useCallback(function () {
    resetState();
  }, [resetState]);

  // Handle keyboard interactions with the text input field
  var inputKeyDownCallback = useCallback(function (e) {
    // Make sure we do not clobber keydown set directly on component
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }

    // Close dropdown on tab
    if (e.keyCode === KEY_CODES.TAB) {
      resetState();
      return;
    }

    // Open the dropdown and prevent up/down from scrolling the page
    if (e.keyCode === KEY_CODES.ARROW_DOWN || e.keyCode === KEY_CODES.ARROW_UP) {
      setIsDropdownOpen(true);
      e.preventDefault();
    }

    // Send keys to listbox for keyboard navigation and selection
    var handled = false;
    if (listboxRef.current) {
      handled = listboxRef.current.handleKeyboardNavigation(e);
    }

    // Send keys to listbox for typeahead support
    if (listboxRef.current && !handled && !editable) {
      listboxRef.current.handleTypeahead(e);
    }

    // We need to handle enter ourselves to support things like freeFrom input
    if (e.keyCode === KEY_CODES.ENTER) {
      // We do not want enter to cause submit while dropdown is open. Listbox will already
      // do this when the event is handled.
      if (isDropdownOpen && !allowSubmitWhileOpen) {
        e.preventDefault();
      }
      var nextValue = inputValue.trim();
      if (!activeOption && freeForm && typeof nextValue === 'string' && nextValue) {
        selectValue(nextValue, {
          reason: COMBOBOX_EVENT_REASON.FREEFORM
        });
      }
    }
  }, [activeOption, allowSubmitWhileOpen, onKeyDown, resetState, freeForm, selectValue, inputValue, isDropdownOpen, editable]);

  // Keep track of active descendant changes
  var optionFocusCallback = useCallback(function (option, meta) {
    setActiveOption(option);
    setActiveReason(meta && meta.reason);
    if (typeof onOptionFocus === 'function') {
      onOptionFocus(option, meta);
    }
  }, [onOptionFocus]);

  // Remove value when tag is removed
  var tagCloseCallback = useCallback(function (tag) {
    var tagValue = getSelectedOptionValue(tag);
    selectValue(tagValue, {
      reason: COMBOBOX_EVENT_REASON.CLEAR_TAG,
      option: typeof valueToOption[tagValue] !== 'undefined' ? valueToOption[tagValue] : {
        label: tag,
        value: tagValue
      }
    });
  }, [selectValue, valueToOption, getSelectedOptionValue]);

  // Reset the entire field when clear is called
  var clearCallback = useCallback(function () {
    clearValue();
  }, [clearValue]);

  // Listen for dropdown open events, e.g. "openWith"
  var dropdownOpenCallback = useCallback(function () {
    setIsDropdownOpen(true);
  }, []);

  // Listen for dropdown close events, e.g. outsideClick, esc
  var dropdownCloseCallback = useCallback(function () {
    setIsDropdownOpen(false);
    setActiveOption(null);
    setActiveReason(null);
  }, []);

  // Prevents focus from moving to the dropdown
  var dropdownMouseDownCallback = useCallback(function (e) {
    e.preventDefault();
  }, []);

  // Update focus whenever we re-render.
  // Note: We need to do this every render because the listbox may not be present when the focus
  // pattern changes, for example if the `loading` prop is enabled.
  useEffect(function () {
    if (listboxRef.current && focusPattern &&
    // Do not change focus if that active option is already intentional
    !activeReason) {
      listboxRef.current.focusPattern(focusPattern);
    }
  });

  // update the dropdown width if
  useIsomorphicLayoutEffect(function () {
    if (isDropdownPortal) {
      setDropdownWidth(inputResizeObserver === null || inputResizeObserver === void 0 ? void 0 : inputResizeObserver.width);
    }
  }, [inputResizeObserver, isDropdownPortal]);

  // Update popper position in case the input size changed when value changed
  useDidChangeCallback(value, function () {
    if (popperRef && popperRef.current && popperRef.current.popper) {
      popperRef.current.popper.update();
    }
  }, []);

  // Update popper position in case the number of options changed
  useDidChangeCallback(options.length, function () {
    if (popperRef && popperRef.current && popperRef.current.popper) {
      popperRef.current.popper.update();
    }
  }, []);

  // Update value via controlled overwrite
  useDidChangeCallback(value, function () {
    // Check if new value and input values are strings and not matching
    var shouldUpdateString = typeof value === 'string' && typeof inputValue === 'string' && value.trim() !== inputValue.trim();

    // check if new value and controlledValue are arrays and not matching
    var shouldUpdateArrayOfStrings = Array.isArray(value) && _toConsumableArray(value).sort().join(',') !== _toConsumableArray(controlledValue).sort().join(',');
    if (shouldUpdateString || shouldUpdateArrayOfStrings) {
      // Update internal value with controlled overwrite meta
      selectValue(value, {
        reason: COMBOBOX_EVENT_REASON.CONTROLLED_OVERWRITE,
        option: typeof valueToOption[value] !== 'undefined' ? valueToOption[value] : {
          value: value
        }
      });
      // Update controlled value
      setControlledValue(value);
    }
  }, [controlledValue, value, inputValue]);

  /** *******
   * RENDER *
   ******** */

  var renderOptionCallback = useCallback(function (optionProps) {
    return /*#__PURE__*/React.createElement(ComboboxOption, _extends({
      highlightPattern: highlightPattern
    }, optionProps));
  }, [highlightPattern]);

  // Build the dropdown content
  var dropdownContent;
  if (!isDropdownOpen) {
    dropdownContent = '';
  } else if (loading) {
    dropdownContent = typeof renderEmptyState === 'function' ? renderLoadingState() : '';
  } else if (!options.length) {
    dropdownContent = typeof renderEmptyState === 'function' ? renderEmptyState() : '';
  } else {
    dropdownContent = /*#__PURE__*/React.createElement(ControlledListbox, {
      activeValue: activeOption ? activeOption.value : null,
      focusOnHover: true,
      id: listboxId,
      mapOptionToProps: null,
      onOptionFocus: optionFocusCallback,
      onOptionSelect: optionSelectCallback,
      options: options,
      ref: listboxRef,
      renderGroup: renderGroup,
      renderGroupLabel: renderGroupLabel,
      renderOption: typeof renderOption === 'function' ? renderOption : renderOptionCallback,
      selectCallbackFrequency: CALLBACK_FREQUENCY.ALWAYS,
      selectOnFocus: false,
      selectWithEnter: true,
      selectWithSpace: selectWithSpace,
      tabIndex: undefined,
      value: value
    });
  }

  // Override `dialog` role (CDS-2791)
  var dropdown = /*#__PURE__*/React.createElement(Dropdown, {
    role: "presentation",
    tabIndex: undefined,
    shouldReturnFocus: false,
    portal: isDropdownPortal,
    shouldIgnoreTriggerClick: true,
    onMouseDown: dropdownMouseDownCallback,
    style: {
      width: isDropdownPortal && fluidDropdown ? dropdownWidth : defaultDropdownWidth
    }
  }, dropdownContent);
  var inputProps = Object.assign({}, rest, {
    appearance: appearance,
    size: size,
    'aria-activedescendant': activeOption && isDropdownOpen ? activeOption.id : undefined,
    'aria-autocomplete': ariaAutocomplete,
    'aria-controls': isDropdownOpen ? listboxId : undefined,
    'autoComplete': AUTOFILL_DISABLE,
    'editable': editable,
    'expanded': isDropdownOpen,
    'limitTagsShown': !isDropdownOpen ? limitTagsShown : undefined,
    'onBlur': inputBlurCallback,
    'onChange': inputChangeCallback,
    'onClear': clearCallback,
    'onKeyDown': inputKeyDownCallback,
    'onTagClose': tagCloseCallback,
    'role': 'combobox',
    'tags': isMultiselectable && value.length ? value.map(function (v) {
      return showLabelForValue ? getSelectedOptionLabel(v) : v;
    }) : undefined,
    'value': showLabelForValue ? getSelectedOptionLabel(inputValue) : inputValue,
    'renderTag': renderTag
  });
  return /*#__PURE__*/React.createElement(StyledCombobox, {
    className: className,
    ref: innerRef,
    fluidDropdown: fluidDropdown
  }, /*#__PURE__*/React.createElement(DropdownPopper, {
    "aria-haspopup": "listbox",
    isOpen: !!dropdownContent && isDropdownOpen,
    onClose: dropdownCloseCallback,
    onOpen: dropdownOpenCallback,
    openWith: ['click', 'focus'],
    ref: popperRef,
    shouldFocusTriggered: false,
    triggered: dropdown
  }, typeof renderInput === 'function' ? renderInput(inputProps) : null));
});
ControlledCombobox.displayName = 'ControlledCombobox';

// PROPS ARE DOCUMENTED IN Combobox.jsx
ControlledCombobox.propTypes = process.env.NODE_ENV !== "production" ? {
  allowSubmitWhileOpen: PropTypes.bool,
  appearance: PropTypes.oneOf(['input', 'select']),
  autoCompleteBehavior: PropTypes.oneOf(['none', 'manual', 'automatic']),
  isDropdownPortal: PropTypes.bool,
  className: PropTypes.string,
  editable: PropTypes.bool,
  fluidDropdown: PropTypes.bool,
  focusFirstOption: PropTypes.bool,
  freeForm: PropTypes.bool,
  getHighlightPattern: PropTypes.func,
  limitTagsShown: PropTypes.number,
  loading: PropTypes.bool,
  mapOptionToProps: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onOptionFocus: PropTypes.func,
  onOptionSelect: PropTypes.func,
  optionFilter: PropTypes.func,
  // eslint-disable-next-line zillow/react/forbid-prop-types
  options: PropTypes.array.isRequired,
  renderEmptyState: PropTypes.func,
  renderGroup: PropTypes.func,
  renderGroupLabel: PropTypes.func,
  renderInput: PropTypes.func,
  renderTag: PropTypes.func,
  renderLoadingState: PropTypes.func,
  renderOption: PropTypes.func,
  selectCallbackFrequency: PropTypes.oneOf(Object.values(CALLBACK_FREQUENCY)),
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  showLabelForValue: PropTypes.bool
} : {};
ControlledCombobox.defaultProps = {
  appearance: 'select',
  autoCompleteBehavior: 'automatic',
  isDropdownPortal: false,
  editable: true,
  fluidDropdown: true,
  optionFilter: function optionFilter(option, inputValue) {
    if (inputValue) {
      // Case-insensitive exact partial match
      return new RegExp(escapeRegex(inputValue.trim()), 'i').test(option.label);
    }
    return true;
  },
  renderEmptyState: function renderEmptyState() {
    return /*#__PURE__*/React.createElement(ComboboxEmptyState, null);
  },
  renderLoadingState: function renderLoadingState() {
    return /*#__PURE__*/React.createElement(ComboboxLoadingState, null);
  },
  renderInput: function renderInput(props) {
    return /*#__PURE__*/React.createElement(ComboboxInput, props);
  },
  renderTag: function renderTag(props) {
    return /*#__PURE__*/React.createElement(Tag, props);
  },
  selectCallbackFrequency: CALLBACK_FREQUENCY.CHANGE,
  size: INPUT_SIZES.md,
  showLabelForValue: false
};